import { useContext, useEffect, useRef } from 'react';
import { GuideContext, type GuideState } from './guideContext';
import type { GuideRegistration } from './types';

export function useGuide(): GuideState {
  const ctx = useContext(GuideContext);
  if (!ctx) throw new Error('useGuide must be used inside a GuideProvider');
  return ctx;
}

/**
 * Optional variant for pages: returns null outside a provider instead of
 * throwing, so a page's own component tests do not have to wrap in one.
 */
export function useGuideOptional(): GuideState | null {
  return useContext(GuideContext);
}

/**
 * How a page tells the Guide about itself.
 *
 * `registration.findings` is a function, called by the panel while it is open,
 * so a closed panel costs nothing and an open one always reads current state
 * without the page having to push updates.
 *
 * `deps` behave exactly like useEffect's, with the same hazard: an unstable
 * entry (a fresh object or array literal each render) re-registers on every
 * render, and since registering re-renders the provider's consumers, that is
 * an infinite loop rather than merely wasteful. Pass values, memos or state.
 *
 * The builder may return **null** for "nothing to register yet". Detail pages
 * guard on a loading/error state before they have a record, and a hook cannot
 * live below an early return — so returning null is how a page says "still
 * loading" without moving the call.
 */
export function useGuideRegistration(build: () => GuideRegistration | null, deps: unknown[]): void {
  const guide = useGuideOptional();
  const register = guide?.register;

  // The builder closes over live page state and is rebuilt every render; the
  // ref keeps the effect from re-registering on each one.
  const buildRef = useRef(build);
  buildRef.current = build;

  useEffect(() => {
    if (!register) return;
    const registration = buildRef.current();
    if (!registration) return;
    register(registration);
    return () => register(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [register, ...deps]);
}
